import type { SessionMeasurementRow } from '../api/sessions';

function csvEscape(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function toCsv(rows: SessionMeasurementRow[]): string {
  const headers = [
    'timestamp', 'lat', 'lon',
    'rsrp', 'rsrq', 'sinr',
    'ci', 'pci', 'tac', 'earfcn',
    'network_generation', 'timing_advance',
    'mcc', 'mnc', 'operator',
    'download_mbps', 'upload_mbps', 'latency_ms', 'mos',
  ];
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push([
      r.ts, r.lat, r.lon,
      r.rsrp, r.rsrq, r.sinr,
      r.ci, r.pci, r.tac, r.earfcn,
      r.networkGeneration, r.timingAdvance,
      r.mcc, r.mnc, r.operator,
      r.downloadMbps, r.uploadMbps, r.latencyMs, r.mos,
    ].map(csvEscape).join(','));
  }
  return lines.join('\n') + '\n';
}

function xmlEscape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;',
  }[c]!));
}

function haversineMeters(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat); const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

function cleanPath(rows: SessionMeasurementRow[]): SessionMeasurementRow[] {
  const v = rows
    .filter((r) => r.lat != null && r.lon != null && r.ts)
    .sort((a, b) => new Date(a.ts as string).getTime() - new Date(b.ts as string).getTime());
  const out: SessionMeasurementRow[] = [];
  for (const r of v) {
    const last = out[out.length - 1];
    if (!last) { out.push(r); continue; }
    if (last.lat === r.lat && last.lon === r.lon) continue;
    const dist = haversineMeters({ lat: last.lat as number, lon: last.lon as number }, { lat: r.lat as number, lon: r.lon as number });
    const dt = (new Date(r.ts as string).getTime() - new Date(last.ts as string).getTime()) / 1000;
    if (dist > 500 && dt < 2) continue;
    out.push(r);
  }
  return out;
}

export function toKml(rows: SessionMeasurementRow[], sessionId: string): string {
  const valid = cleanPath(rows);
  const coords = valid.map((r) => `${r.lon},${r.lat},0`).join(' ');
  const placemarks = valid.map((r) => {
    const data = [
      ['rsrp', r.rsrp], ['rsrq', r.rsrq], ['sinr', r.sinr],
      ['ci', r.ci], ['pci', r.pci], ['tac', r.tac], ['earfcn', r.earfcn],
      ['networkGeneration', r.networkGeneration],
      ['operator', r.operator],
      ['downloadMbps', r.downloadMbps], ['uploadMbps', r.uploadMbps],
      ['latencyMs', r.latencyMs], ['mos', r.mos],
    ];
    return `<Placemark>
  <name>${xmlEscape(r.ts ?? '')}</name>
  <Point><coordinates>${r.lon},${r.lat},0</coordinates></Point>
  <ExtendedData>${data.map(([k, v]) => `<Data name="${k}"><value>${xmlEscape(v == null ? '' : String(v))}</value></Data>`).join('')}</ExtendedData>
</Placemark>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>DriveTest session ${xmlEscape(sessionId)}</name>
    <Placemark>
      <name>Path</name>
      <Style><LineStyle><color>ff22c55e</color><width>3</width></LineStyle></Style>
      <LineString><tessellate>1</tessellate><coordinates>${coords}</coordinates></LineString>
    </Placemark>
${placemarks}
  </Document>
</kml>
`;
}

export function downloadBlob(filename: string, contents: string, mime: string): void {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
